import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Toolbar,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { academicYearsAPI } from '../../services/api';

export default function AdminAcademicYears() {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    year_name: '',
    start_date: '',
    end_date: '',
    is_current: 0,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' });

  useEffect(() => {
    fetchYears();
  }, []);

  const fetchYears = async () => {
    try {
      setLoading(true);
      const response = await academicYearsAPI.getAll();
      setYears(response.data);
    } catch (error) {
      showSnackbar('Error loading academic years', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, type = 'success') => {
    setSnackbar({ open: true, message, type });
  };

  const handleOpenDialog = (year = null) => {
    if (year) {
      setEditingId(year.id);
      setFormData({
        year_name: year.year_name,
        start_date: year.start_date,
        end_date: year.end_date,
        is_current: year.is_current,
      });
    } else {
      setEditingId(null);
      setFormData({
        year_name: '',
        start_date: '',
        end_date: '',
        is_current: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'is_current' ? parseInt(value) : value,
    });
  };

  const handleSave = async () => {
    if (!formData.year_name || !formData.start_date || !formData.end_date) {
      showSnackbar('All fields are required', 'error');
      return;
    }

    if (new Date(formData.start_date) >= new Date(formData.end_date)) {
      showSnackbar('End date must be after start date', 'error');
      return;
    }

    try {
      if (editingId) {
        await academicYearsAPI.update(editingId, formData);
        showSnackbar('Academic year updated successfully', 'success');
      } else {
        await academicYearsAPI.create(formData);
        showSnackbar('Academic year created successfully', 'success');
      }
      fetchYears();
      handleCloseDialog();
    } catch (error) {
      showSnackbar('Error saving academic year', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this academic year?')) return;

    try {
      await academicYearsAPI.delete(id);
      showSnackbar('Academic year deleted successfully', 'success');
      fetchYears();
    } catch (error) {
      showSnackbar('Error deleting academic year', 'error');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Toolbar sx={{ paddingLeft: 0, paddingRight: 0, marginBottom: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Academic Year
        </Button>
      </Toolbar>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Year Name</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell align="center">Current</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {years.length > 0 ? (
                years.map((year) => (
                  <TableRow key={year.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{year.year_name}</TableCell>
                    <TableCell>{formatDate(year.start_date)}</TableCell>
                    <TableCell>{formatDate(year.end_date)}</TableCell>
                    <TableCell align="center">
                      {year.is_current ? (
                        <Chip label="Current" color="primary" size="small" />
                      ) : (
                        <span>-</span>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(year)}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(year.id)}
                        title="Delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ padding: 3 }}>
                    No academic years found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Academic Year' : 'Add New Academic Year'}</DialogTitle>
        <DialogContent sx={{ paddingTop: 2 }}>
          <TextField
            fullWidth
            label="Year Name"
            name="year_name"
            value={formData.year_name}
            onChange={handleInputChange}
            placeholder="e.g., 2025-2026"
            sx={{ marginBottom: 2 }}
          />
          <TextField
            fullWidth
            label="Start Date"
            name="start_date"
            type="date"
            value={formData.start_date}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
            sx={{ marginBottom: 2 }}
          />
          <TextField
            fullWidth
            label="End Date"
            name="end_date"
            type="date"
            value={formData.end_date}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
            sx={{ marginBottom: 2 }}
          />
          <TextField
            fullWidth
            label="Set as Current"
            name="is_current"
            select
            value={formData.is_current}
            onChange={handleInputChange}
            SelectProps={{
              native: true,
            }}
          >
            <option value={0}>No</option>
            <option value={1}>Yes</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.type}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
